import { useEffect, useState } from "react";
import { api } from "../../api";
import type { OrganizationSettings, ProviderStatus, UserPreferences } from "../../../shared/types";
import { useSession } from "../../shell/session";
import { ErrorNote } from "../../ui";

interface SettingsPayload {
  organization: OrganizationSettings;
  preferences: UserPreferences;
  providers: ProviderStatus;
  role: string;
}

interface TesterAccount {
  id: string;
  email: string;
  name: string;
  role: string;
  disabledAt: string | null;
}

interface OneTimeCredential { email: string; password: string }

export function SettingsPage() {
  const { user, refresh } = useSession();
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [accounts, setAccounts] = useState<TesterAccount[]>([]);
  const [oneTime, setOneTime] = useState<OneTimeCredential | null>(null);

  async function loadAccounts() {
    setAccounts(await api<TesterAccount[]>("/api/v1/settings/users"));
  }

  useEffect(() => {
    void api<SettingsPayload>("/api/v1/settings").then(setSettings).catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(() => {
    if (user?.role === "owner") void loadAccounts().catch((caught: Error) => setError(caught.message));
  }, [user?.role]);

  if (!settings) return <section className="page">{error ? <ErrorNote>{error}</ErrorNote> : "Loading settings…"}</section>;
  const owner = user?.role === "owner";

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Workspace</p>
          <h1 className="page-title">Settings</h1>
          <p className="lede">Manage your workspace and display. Email, SMS, and card payments are not connected.</p>
        </div>
      </header>
      {error && <ErrorNote>{error}</ErrorNote>}
      {saved && <p className="notice">{saved}</p>}
      <form className="panel panel-pad form-grid" onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        void api("/api/v1/settings/organization", {
          method: "PATCH",
          body: JSON.stringify({
            name: String(form.get("name") ?? ""),
            address: String(form.get("address") ?? ""),
            publicWelcome: String(form.get("welcome") ?? ""),
          }),
        }).then(() => setSaved("Organisation details saved.")).catch((caught: Error) => setError(caught.message));
      }}>
        <h2>Organisation</h2>
        <div className="field"><label htmlFor="org-name">Name</label><input id="org-name" name="name" defaultValue={settings.organization.name} disabled={!owner} /></div>
        <div className="field"><label htmlFor="org-address">Address</label><input id="org-address" name="address" defaultValue={settings.organization.address} disabled={!owner} /></div>
        <div className="field"><label htmlFor="org-welcome">Public welcome</label><textarea id="org-welcome" name="welcome" defaultValue={settings.organization.publicWelcome} disabled={!owner} /></div>
        <p className="muted">Timezone {settings.organization.timezone}. Dates use en-NZ.</p>
        {owner ? <button className="btn primary" type="submit">Save organisation</button> : <p className="muted">Only the owner can change organisation details.</p>}
      </form>
      <form className="panel panel-pad form-grid" onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        void api("/api/v1/settings/preferences", {
          method: "PATCH",
          body: JSON.stringify({
            density: form.get("density"),
            contrast: form.get("contrast"),
            standFont: Number(form.get("standFont")),
          }),
        }).then(async () => { setSaved("Display preferences saved."); await refresh(); }).catch((caught: Error) => setError(caught.message));
      }}>
        <h2>Your display</h2>
        <div className="field"><label htmlFor="density">Density</label>
          <select id="density" name="density" defaultValue={settings.preferences.density}><option value="comfortable">Comfortable</option><option value="compact">Compact</option></select>
        </div>
        <div className="field"><label htmlFor="contrast">Contrast</label>
          <select id="contrast" name="contrast" defaultValue={settings.preferences.contrast}><option value="default">Default</option><option value="high">High contrast</option></select>
        </div>
        <div className="field"><label htmlFor="stand-font">Stand font size</label><input id="stand-font" name="standFont" type="number" min={16} max={48} defaultValue={settings.preferences.standFont} /></div>
        <button className="btn" type="submit">Save display</button>
      </form>
      <form className="panel panel-pad form-grid" onSubmit={(event) => {
        event.preventDefault();
        setError("");
        const form = new FormData(event.currentTarget);
        void api("/api/v1/auth/change-password", { method: "POST", body: JSON.stringify({
          currentPassword: String(form.get("currentPassword") ?? ""),
          newPassword: String(form.get("newPassword") ?? ""),
        }) }).then(() => { window.location.assign("/"); }).catch((caught: Error) => setError(caught.message));
      }}>
        <h2>Change your password</h2>
        <div className="field"><label htmlFor="current-password">Current password</label><input id="current-password" name="currentPassword" type="password" autoComplete="current-password" required /></div>
        <div className="field"><label htmlFor="new-password">New password</label><input id="new-password" name="newPassword" type="password" autoComplete="new-password" minLength={12} required /></div>
        <p className="muted">Use at least 12 characters. You will sign in again after changing it.</p>
        <button className="btn" type="submit">Change password</button>
      </form>
      {owner && <section className="panel panel-pad stack">
        <h2>Tester accounts</h2>
        <p className="muted">Create a separate login for each tester. MB Live shows a temporary password once; share it with that person through your own secure channel.</p>
        <form className="form-grid" onSubmit={(event) => {
          event.preventDefault();
          setError("");
          setOneTime(null);
          const formElement = event.currentTarget;
          const form = new FormData(event.currentTarget);
          void api<OneTimeCredential>("/api/v1/settings/users", { method: "POST", body: JSON.stringify({
            name: String(form.get("name") ?? ""),
            email: String(form.get("email") ?? ""),
            role: String(form.get("role") ?? "member"),
          }) }).then(async (result) => {
            setOneTime(result);
            setSaved("Tester account created. Copy the temporary password now.");
            await loadAccounts();
            formElement.reset();
          }).catch((caught: Error) => setError(caught.message));
        }}>
          <div className="field"><label htmlFor="tester-name">Name</label><input id="tester-name" name="name" required minLength={2} /></div>
          <div className="field"><label htmlFor="tester-email">Email</label><input id="tester-email" name="email" type="email" required /></div>
          <div className="field"><label htmlFor="tester-role">Access</label><select id="tester-role" name="role" defaultValue="member"><option value="member">Member</option><option value="volunteer">Volunteer</option><option value="leader">Music leader</option><option value="coordinator">Coordinator</option></select></div>
          <button className="btn primary" type="submit">Create tester account</button>
        </form>
        {oneTime && <div className="notice" role="status">
          <strong>Temporary sign-in for {oneTime.email}</strong>
          <p><code>{oneTime.password}</code></p>
          <button className="btn" type="button" onClick={() => void navigator.clipboard.writeText(`${oneTime.email}\n${oneTime.password}`).then(() => setSaved("Sign-in details copied.")).catch(() => setError("Could not copy. Select the password above instead."))}>Copy sign-in details</button>
          <button className="btn ghost" type="button" onClick={() => setOneTime(null)}>Hide password</button>
        </div>}
        <div className="stack">
          {accounts.map((account) => <div className="cluster" key={account.id}>
            <span><strong>{account.name}</strong> · {account.email} · {account.role}{account.disabledAt ? " · disabled" : ""}</span>
            {account.role !== "owner" && <>
              <button className="btn" type="button" onClick={() => {
                setOneTime(null);
                void api<OneTimeCredential>(`/api/v1/settings/users/${account.id}/reset-password`, { method: "POST" }).then((result) => { setOneTime(result); setSaved("Password reset. Copy the new password now."); }).catch((caught: Error) => setError(caught.message));
              }}>Reset password</button>
              <button className="btn ghost" type="button" onClick={() => {
                void api(`/api/v1/settings/users/${account.id}/status`, { method: "PATCH", body: JSON.stringify({ disabled: !account.disabledAt }) }).then(async () => { await loadAccounts(); setSaved(account.disabledAt ? "Tester access enabled." : "Tester access disabled."); }).catch((caught: Error) => setError(caught.message));
              }}>{account.disabledAt ? "Enable" : "Disable"}</button>
            </>}
          </div>)}
        </div>
      </section>}
      <article className="panel panel-pad stack">
        <h2>Providers</h2>
        <p>Email: not configured. Drafts stay in the workspace outbox.</p>
        <p>SMS: not configured. Nothing is texted.</p>
        <p>Payments: offline ledger only. No card numbers and no live charges.</p>
        <div className="cluster">
          <a className="btn" href="/api/v1/exports/donations.csv">Download giving CSV</a>
          <a className="btn" href="/api/v1/exports/people.csv">Download people CSV</a>
        </div>
        <p className="faint">Exports require a coordinator or owner session. The browser sends your sign-in cookie.</p>
      </article>
    </section>
  );
}
